/**
 * sl-8fwi.4: Workflow-switch handler — pure async function extracted from
 * the React `ProcessTab.handleWorkflowChange` callback so the side-effect
 * orchestration can be unit-tested without mounting the whole sidebar.
 *
 * sl-g3on.3.2 originally lived as a useCallback closure inside
 * `process-tab.tsx`. The 3x Phase 4b audit found two correctness gaps
 * (#60 KEEP-vs-CLEAR drift, #61 onClear bypassing the cleanup pipeline)
 * AND a test-coverage gap (#63 — the existing spec only exercised the
 * pure `runSwitchCleanup` helper, never the integration around it). The
 * extraction here lets the spec drive the actual handler with mock
 * dependencies and pin every branch.
 *
 * SOLID:
 * - S: Single responsibility — orchestrate the four switch sub-steps
 *      (snapshot old → load new → compute cleanup → commit).
 * - D: Every collaborator (loadSchema, commit, notify) is injected so
 *      the function has zero React/SDK couplings.
 *
 * Branches:
 * 1. workflow === null            → "clear" path, drop everything, notify
 * 2. loadSchema returns null       → KEEP existing mappings, notify schema-load-failed
 * 3. loadSchema throws             → KEEP existing mappings, notify schema-load-failed
 * 4. loadSchema returns a schema   → run computeMappingCleanup, commit pruned set
 */

import type { InputMapping, OutputMapping } from '../services/automation';
import type { WorkflowInfo, WorkflowSchema } from '../services/workflow';
import {
  computeMappingCleanup,
  fromInputRequiredOptional,
  fromOutputRecord,
  type MappingCleanupResult,
} from './workflow-mapping-diff';

/** Outcome flag passed to the commit callback so the UI knows what to render. */
export type WorkflowSwitchCommitMode =
  | 'success' // schema loaded + cleanup applied
  | 'schema-load-failed' // schema fetch returned null OR threw, mappings KEPT
  | 'cleared'; // workflow === null, all mappings dropped

export interface WorkflowSwitchDeps {
  /** The new workflow the user picked. `null` means "clear". */
  workflow: WorkflowInfo | null;
  /** Snapshot of the OLD schema BEFORE the user switched (may be null). */
  oldSchema: WorkflowSchema | null;
  /** Read at commit time: table selection may change while the schema is loading. */
  readMappings: () => {
    currentInputs: ReadonlyArray<InputMapping>;
    currentOutputs: ReadonlyArray<OutputMapping>;
  };
  /** Async loader for the new workflow's schema. May reject; may resolve null. */
  loadSchema: (workflowId: string) => Promise<WorkflowSchema | null>;
  /**
   * Commit hook — receives the cleanup result, the mode discriminator,
   * and the new workflow id/name (or `null` for the clear path). The
   * caller wraps this in `onUpdate` + i18n notification.
   */
  commit: (
    cleanup: MappingCleanupResult,
    mode: WorkflowSwitchCommitMode,
    workflow: WorkflowInfo | null
  ) => void;
}

/**
 * Run the workflow-switch pipeline. Never throws — every error mode is
 * folded back into a `commit` call with the appropriate `mode`. The
 * caller is responsible for actually applying `cleanup` to the
 * automation config and rendering the user-facing notification.
 */
export async function executeWorkflowSwitch(
  deps: WorkflowSwitchDeps
): Promise<void> {
  const { workflow, oldSchema, readMappings, loadSchema, commit } = deps;

  // ─── Branch 1: clear path ─────────────────────────────────────────────
  // sl-8fwi.4 (#61 fix): The clear path (X-button on WorkflowSelector)
  // used to live as a separate `onClear` handler that called `onUpdate`
  // directly. That bypassed the cleanup audit trail entirely and the
  // user got no transparency on what just happened. Now it goes through
  // the same `commit` callback with `mode='cleared'`, and the caller
  // emits a "removed N mappings" toast just like a normal switch.
  if (workflow === null) {
    const { currentInputs, currentOutputs } = readMappings();
    commit(
      {
        cleanInputMappings: [],
        cleanOutputMappings: [],
        removed: {
          inputs: currentInputs.map(mapping => ({
            mapping,
            reason: 'field-missing' as const,
          })),
          outputs: currentOutputs.map(mapping => ({
            mapping,
            reason: 'field-missing' as const,
          })),
        },
      },
      'cleared',
      null
    );
    return;
  }

  // ─── Branches 2-4: schema fetch ──────────────────────────────────────
  let newSchema: WorkflowSchema | null;
  try {
    newSchema = await loadSchema(workflow.id);
  } catch {
    newSchema = null;
  }
  const { currentInputs, currentOutputs } = readMappings();

  if (!newSchema) {
    // sl-8fwi.4 (#60 fix): Same KEEP-on-failure semantics for the
    // resolves-null path. Pre-fix this branch built a remove-everything
    // cleanup too — both failure shapes are now consistent.
    commit(
      {
        cleanInputMappings: [...currentInputs],
        cleanOutputMappings: [...currentOutputs],
        removed: { inputs: [], outputs: [] },
      },
      'schema-load-failed',
      workflow
    );
    return;
  }

  // ─── Branch 4: happy path — diff old vs new schema ───────────────────
  const oldInput = oldSchema
    ? fromInputRequiredOptional(
        oldSchema.input.required,
        oldSchema.input.optional
      )
    : null;
  const oldOutput = oldSchema ? fromOutputRecord(oldSchema.output) : null;
  const newInput = fromInputRequiredOptional(
    newSchema.input.required,
    newSchema.input.optional
  );
  const newOutput = fromOutputRecord(newSchema.output);

  const cleanup = computeMappingCleanup(
    oldInput,
    newInput,
    oldOutput,
    newOutput,
    [...currentInputs],
    [...currentOutputs]
  );

  commit(cleanup, 'success', workflow);
}
